import React, { useEffect } from 'react';
import FilterSelect from '@/components/filters/FilterSelect';
import Search from '@/components/filters/Search';
import UsersTable from './components/UsersTable';
import ConfirmDialog from '@/components/dialog/ConfirmDialog';
import SuccessDialog from '@/components/dialog/SuccessDialog';
import { User } from './types';
import { Icons } from '@/icons';
import AnalyticsCard from '@/components/cards/AnalyticsCard';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllUsersAdmin,
  fetchTotalUsersAnalytics,
  softDeleteUser,
} from '@/store/User';
import { Pagination } from '@/types/Pagination.type';
import { Loader } from 'lucide-react';
import { RootState } from '@/store';

type TotalUsersData = {
  females: number;
  males: number;
  others: number;
  total_users: number;
};

const Users: React.FC = () => {
  const [status, setStatus] = React.useState('All');
  const [gender, setGender] = React.useState('All');
  const [search, setSearch] = React.useState('');
  // ADMIN-SIGNUPS-V1: join-date range + sort. Empty dates = all time.
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [sort, setSort] = React.useState<'newest' | 'oldest'>('newest');
  const [users, setUsers] = React.useState<User[]>([]);
  const [totalUsersData, setTotalUsersData] = React.useState<TotalUsersData>();
  const [pagination, setPagination] = React.useState<Pagination>();
  const [page, setPage] = React.useState(1);

  // Delete confirmation state
  const [deleteIdx, setDeleteIdx] = React.useState<string | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
  const [isDeleteSuccessOpen, setIsDeleteSuccessOpen] = React.useState(false);

  const dispatch = useDispatch<any>();
  const { status: sliceStatus, error } = useSelector((s: RootState) => s.users);

  const buildParams = (p: number) => ({
    gender: gender === 'All' ? undefined : gender,
    name: search || undefined,
    status: status.toLowerCase() === 'all' ? undefined : status.toLowerCase(),
    from: from || undefined,
    to: to || undefined,
    sort,
    page: p,
    limit: 20,
  });

  /* ─── analytics on mount ──────────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      const res = await dispatch(fetchTotalUsersAnalytics());
      setTotalUsersData(res?.payload?.data?.[0]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── filters/search change → reset & fetch page 1 ────────────────────── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPage(1);
      setUsers([]);
      const res = await dispatch(fetchAllUsersAdmin(buildParams(1)));
      if (cancelled) return;
      setUsers(res?.payload?.data ?? []);
      setPagination(res?.payload?.pagination);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, gender, from, to, sort]);

  /* ─── infinite-scroll callback ────────────────────────────────────────── */
  const totalPages = pagination?.totalPages ?? 1;

  const loadMore = async () => {
    if (sliceStatus === 'loading') return;
    if (page >= totalPages) return;

    const next = page + 1;
    setPage(next); // optimistic increment
    const res = await dispatch(fetchAllUsersAdmin(buildParams(next)));
    const nextData: User[] = res?.payload?.data ?? [];
    const nextPg: Pagination | undefined = res?.payload?.pagination;

    setUsers((prev) => [...prev, ...nextData]); // append
    if (nextPg) setPagination(nextPg);
  };

  /* ─── delete flows ───────────────────────────────────────────────────── */
  const openDelete = (idx: string) => {
    setDeleteIdx(idx);
    setIsDeleteOpen(true);
  };

  const closeDelete = () => {
    setIsDeleteOpen(false);
    setDeleteIdx(null);
  };

  const confirmDelete = async () => {
    if (!deleteIdx) return;
    await dispatch(softDeleteUser(deleteIdx));
    setUsers((curr) => curr.filter((u) => u.id !== deleteIdx));
    closeDelete();
    setIsDeleteSuccessOpen(true);
  };

  return (
    <div className="rounded-[24px] flex flex-col h-full min-h-0 space-y-[20px]">
      {/* Top metrics */}
      <div className="grid grid-cols-4 gap-4">
        <AnalyticsCard label="Total Users" value={totalUsersData?.total_users} />
        <AnalyticsCard label="Males" value={totalUsersData?.males} />
        <AnalyticsCard label="Females" value={totalUsersData?.females} />
        <AnalyticsCard label="Others" value={totalUsersData?.others} />
      </div>

      <div className="p-8 rounded-[24px] bg-white flex flex-col h-full min-h-0 space-y-[5px]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* result count */}
          <div className="text-[24px] font-[600]">
            Showing {pagination?.totalItems ?? users.length} Results
          </div>

          {/* Filters */}
          <div className="ml-auto flex flex-wrap items-end gap-[10px]">
            <Search value={search} onChange={(e) => setSearch(e.target.value)} />

            <FilterSelect
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={[
                { value: 'All', label: 'Activity: All' },
                { value: 'active', label: 'Activity: Active' },
                { value: 'inactive', label: 'Activity: Inactive' },
              ]}
            />

            {/* ADMIN-SIGNUPS-V1: join-date range + sort */}
            <label className="flex flex-col text-[12px] text-gray-600">
              Joined from
              <input
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
                className="h-[56px] rounded-[16px] bg-[#F5F5F5] px-4 border border-black focus:outline-none text-[14px] text-black"
              />
            </label>
            <label className="flex flex-col text-[12px] text-gray-600">
              Joined to
              <input
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
                className="h-[56px] rounded-[16px] bg-[#F5F5F5] px-4 border border-black focus:outline-none text-[14px] text-black"
              />
            </label>
            {(from || to) && (
              <button
                type="button"
                onClick={() => {
                  setFrom('');
                  setTo('');
                }}
                className="h-[56px] px-4 rounded-[16px] border border-black text-[14px]"
              >
                Clear dates
              </button>
            )}

            <FilterSelect
              value={sort}
              onChange={(e) => setSort(e.target.value as 'newest' | 'oldest')}
              options={[
                { value: 'newest', label: 'Sort: Newest first' },
                { value: 'oldest', label: 'Sort: Oldest first' },
              ]}
            />

            <FilterSelect
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              options={[
                { value: 'All', label: 'Gender: All' },
                { value: 'male', label: 'Gender: Male' },
                { value: 'female', label: 'Gender: Female' },
                { value: 'other', label: 'Gender: Other' },
              ]}
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0">
          <UsersTable
            data={users}
            onDelete={openDelete}
            loadMore={loadMore}
            hasMore={page < totalPages}
          />
        </div>

        {/* Confirm Delete */}
        <ConfirmDialog
          open={isDeleteOpen}
          title="Delete User"
          message="Are you sure you want to delete this user? You can’t undo this."
          confirmLabel="Yes, Delete"
          cancelLabel="No, Cancel"
          requireAck
          onConfirm={confirmDelete}
          onClose={closeDelete}
        />

        {/* Success Dialog */}
        <SuccessDialog
          open={isDeleteSuccessOpen}
          onClose={() => setIsDeleteSuccessOpen(false)}
          icon={<Icons.Trash className="h-[160px] w-[160px]" />}
          heading="Deleted!"
          subheading="Your user has been deleted."
        />
      </div>
    </div>
  );
};

export default Users;
